/**
 * Button Component - AWLP Design System
 * Supports multiple variants, sizes, loading state, and icons
 */

import { ButtonHTMLAttributes, ReactNode } from 'react';
import styled, { css } from 'styled-components';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'ghost'
  | 'primary-gold'
  | 'primary-blue'
  | 'gradient'
  | 'outlined';

export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  children: ReactNode;
}

const getVariantStyles = (variant: ButtonVariant) => {
  const variants = {
    primary: css`
      background: ${({ theme }) => theme.colors.primary.base};
      color: ${({ theme }) => theme.colors.text.white};
      border: 1px solid ${({ theme }) => theme.colors.primary.base};

      &:hover:not(:disabled) {
        background: ${({ theme }) => theme.colors.primary.dark};
        border-color: ${({ theme }) => theme.colors.primary.dark};
      }

      &:active:not(:disabled) {
        background: ${({ theme }) => theme.colors.primary.dark};
        transform: translateY(1px);
      }
    `,

    secondary: css`
      background: ${({ theme }) => theme.colors.backgrounds.white};
      color: ${({ theme }) => theme.colors.text.primary};
      border: 1px solid ${({ theme }) => theme.colors.borders.light};

      &:hover:not(:disabled) {
        background: ${({ theme }) => theme.colors.backgrounds.light};
        border-color: ${({ theme }) => theme.colors.primary.base};
      }

      &:active:not(:disabled) {
        background: ${({ theme }) => theme.colors.backgrounds.light};
        transform: translateY(1px);
      }
    `,

    danger: css`
      background: ${({ theme }) => theme.colors.status.error.text};
      color: ${({ theme }) => theme.colors.text.white};
      border: 1px solid ${({ theme }) => theme.colors.status.error.text};

      &:hover:not(:disabled) {
        background: ${({ theme }) => theme.colors.status.error.border};
        border-color: ${({ theme }) => theme.colors.status.error.border};
      }

      &:active:not(:disabled) {
        background: ${({ theme }) => theme.colors.status.error.text};
        transform: translateY(1px);
      }
    `,

    ghost: css`
      background: transparent;
      color: ${({ theme }) => theme.colors.text.primary};
      border: 1px solid transparent;

      &:hover:not(:disabled) {
        background: ${({ theme }) => theme.colors.backgrounds.light};
      }

      &:active:not(:disabled) {
        background: ${({ theme }) => theme.colors.backgrounds.light};
        transform: translateY(1px);
      }
    `,

    /* primary-gold: teal accent variant — accent.gold resolves to teal after theme update */
    'primary-gold': css`
      background: ${({ theme }) => theme.colors.accent.gold};
      color: ${({ theme }) => theme.colors.text.primary};
      border: 1px solid ${({ theme }) => theme.colors.accent.gold};

      &:hover:not(:disabled) {
        background: ${({ theme }) => theme.colors.accent.warm};
        border-color: ${({ theme }) => theme.colors.accent.warm};
      }

      &:active:not(:disabled) {
        background: ${({ theme }) => theme.colors.accent.warm};
        transform: translateY(1px);
      }
    `,

    /* primary-blue: teal primary with accent stripe — accent.gold resolves to teal shade after theme update */
    'primary-blue': css`
      background: ${({ theme }) => theme.colors.primary.base};
      color: ${({ theme }) => theme.colors.text.white};
      border: 1px solid ${({ theme }) => theme.colors.primary.base};
      position: relative;
      overflow: hidden;

      &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 4px;
        height: 100%;
        background: ${({ theme }) => theme.colors.accent.gold};
      }

      &:hover:not(:disabled) {
        background: ${({ theme }) => theme.colors.primary.sky};
        border-color: ${({ theme }) => theme.colors.primary.sky};
      }

      &:active:not(:disabled) {
        background: ${({ theme }) => theme.colors.primary.dark};
        transform: translateY(1px);
      }
    `,

    gradient: css`
      background: ${({ theme }) => theme.colors.gradients.button};
      color: ${({ theme }) => theme.colors.text.white};
      border: 1px solid ${({ theme }) => theme.colors.primary.base};

      &:hover:not(:disabled) {
        opacity: 0.9;
        box-shadow: ${({ theme }) => theme.shadows.md};
      }

      &:active:not(:disabled) {
        opacity: 0.95;
        transform: translateY(1px);
      }
    `,

    outlined: css`
      background: transparent;
      color: ${({ theme }) => theme.colors.primary.base};
      border: 1px solid ${({ theme }) => theme.colors.primary.base};

      &:hover:not(:disabled) {
        background: ${({ theme }) => theme.colors.primary.base};
        color: ${({ theme }) => theme.colors.text.white};
      }

      &:active:not(:disabled) {
        background: ${({ theme }) => theme.colors.primary.dark};
        border-color: ${({ theme }) => theme.colors.primary.dark};
        transform: translateY(1px);
      }
    `,
  };

  return variants[variant];
};

const getSizeStyles = (size: ButtonSize) => {
  const sizes = {
    sm: css`
      height: 32px;
      padding: 0 ${({ theme }) => theme.spacing[3]};
      font-size: ${({ theme }) => theme.typography.fontSize.sm};
      gap: ${({ theme }) => theme.spacing[2]};
    `,
    md: css`
      height: 40px;
      padding: 0 ${({ theme }) => theme.spacing[4]};
      font-size: ${({ theme }) => theme.typography.fontSize.base};
      gap: ${({ theme }) => theme.spacing[2]};
    `,
    lg: css`
      height: 48px;
      padding: 0 ${({ theme }) => theme.spacing[6]};
      font-size: ${({ theme }) => theme.typography.fontSize.lg};
      gap: ${({ theme }) => theme.spacing[3]};
    `,
  };

  return sizes[size];
};

const StyledButton = styled.button<{
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth: boolean;
  $isLoading: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: ${({ theme }) => theme.transitions.fast};
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
  position: relative;

  ${({ $variant }) => getVariantStyles($variant)}
  ${({ $size }) => getSizeStyles($size)}

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  ${({ $isLoading }) =>
    $isLoading &&
    css`
      cursor: wait;
      opacity: 0.8;
    `}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
  }
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Spinner = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const Button = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  isLoading = false,
  leftIcon,
  rightIcon,
  children,
  disabled,
  ...props
}: ButtonProps) => {
  return (
    <StyledButton
      $variant={variant}
      $size={size}
      $fullWidth={fullWidth}
      $isLoading={isLoading}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <IconWrapper>
          <Spinner />
        </IconWrapper>
      )}
      {!isLoading && leftIcon && <IconWrapper>{leftIcon}</IconWrapper>}
      {children}
      {!isLoading && rightIcon && <IconWrapper>{rightIcon}</IconWrapper>}
    </StyledButton>
  );
};

export default Button;
